import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";
import IconButton from "./IconButton";
import Spinner from "./Spinner";
import CloseIcon from "@mui/icons-material/Close";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import {
  isDeliveryUnknown,
  type StoveAction,
} from "../../services/stoveCommands";

export type BulkStoveTarget = {
  /** Sensor id used for the API call. */
  name: string;
  /** Unit number shown to the user. */
  displayName?: string;
  /** Current stove state shown in the confirmation list. */
  stateLabel?: string;
};

// No "pending": every command is dispatched at once, so each row is either
// in flight or settled.
type RowStatus = "applying" | "applied" | "unconfirmed" | "failed";

type Row = {
  target: BulkStoveTarget;
  status: RowStatus;
  error?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Which stove command this dialog drives. "lock" re-asserts the lock on a
   *  stove that is off but unlocked (same API call as shutoff). */
  action: StoveAction;
  buildingName?: string;
  targets: BulkStoveTarget[];
  /** Applies the command to one sensor. */
  perform: (name: string) => Promise<unknown>;
  /**
   * Optional confirm-by-state check run after `perform` resolves. The API
   * only fire-and-forgets an MQTT command, so a 2xx says nothing about the
   * device having applied it; this should poll until the sensor reports the
   * expected state (resolve true) or give up (resolve false).
   */
  waitForApplied?: (name: string) => Promise<boolean>;
};

const NOT_CONFIRMED_ERROR =
  "Hasn't confirmed the change yet — it may still apply.";

function getCopy(action: Props["action"], count: number) {
  const single = count === 1;
  switch (action) {
    case "unlock":
      return {
        title: single ? "Unlock Stove" : "Unlock All",
        accent: "text-alert-info",
        confirmBody: (building?: string) =>
          single
            ? "Unlock this stove? It can then be powered on at the appliance."
            : `Unlock all stoves${building ? ` at ${building}` : ""}?`,
        confirmButton: "Unlock",
        buttonVariant: "info" as const,
        titleInProgress: single ? "Unlocking stove…" : "Unlocking stoves…",
        titleDone: single ? "Stove unlocked" : "Stoves unlocked",
        appliedLabel: "Unlocked",
      };
    case "lock":
      return {
        title: "Lock Stove",
        accent: "text-alert-info",
        confirmBody: () =>
          single
            ? "Lock this stove? It will stay off and can't be powered on at the appliance until unlocked."
            : "Lock these stoves? They will stay off and can't be powered on at the appliance until unlocked.",
        confirmButton: "Lock",
        buttonVariant: "info" as const,
        titleInProgress: single ? "Locking stove…" : "Locking stoves…",
        titleDone: single ? "Stove locked" : "Stoves locked",
        appliedLabel: "Locked",
      };
    default:
      return {
        title: single ? "Disable & Lock" : "Disable & Lock All",
        accent: "text-alert-critical",
        confirmBody: (building?: string) =>
          single
            ? "Disable this stove? It will be turned off and locked."
            : `Disable all stoves${building ? ` at ${building}` : ""}? Each one will be turned off and locked.`,
        confirmButton: "Disable & Lock",
        buttonVariant: "danger" as const,
        titleInProgress: single ? "Disabling stove…" : "Disabling stoves…",
        titleDone: single ? "Stove disabled" : "Stoves disabled",
        appliedLabel: "Disabled",
      };
  }
}

const STATUS_CLASS: Record<RowStatus, string> = {
  applying: "text-ink-2",
  applied: "text-alert-success",
  unconfirmed: "text-alert-warning",
  failed: "text-alert-critical",
};

/**
 * Building-level Disable & Lock All / Unlock All flow, mirroring the phone
 * app's bulk-action UX: a confirmation card listing exactly which stoves
 * will be affected (with their current state), then a live per-sensor
 * Applied/Failed results view with an honest failure footer. Replaces the old
 * window.confirm + fire-and-reload flow.
 */
export default function BulkStoveActionModal({
  open,
  onClose,
  action,
  buildingName,
  targets,
  perform,
  waitForApplied,
}: Props) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [applying, setApplying] = useState(false);
  // Height captured at confirm time so the dialog doesn't resize as the
  // content switches between the confirm, progress, and done views.
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const copy = getCopy(action, targets.length);

  // Reset to the confirmation view each time the modal opens.
  useEffect(() => {
    if (open) {
      setRows(null);
      setApplying(false);
      setLockedHeight(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !applying) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, applying]);

  if (!open) return null;

  const applyToAll = async () => {
    if (cardRef.current) setLockedHeight(cardRef.current.offsetHeight);
    // Snapshot the handlers at confirm time so nothing the parent does
    // mid-run (e.g. clearing the state that backs these closures) can change
    // which command this run performs or confirms against.
    const performNow = perform;
    const waitNow = waitForApplied;
    setApplying(true);
    let next: Row[] = targets.map((target) => ({
      target,
      status: "applying" as RowStatus,
    }));
    setRows(next);
    const update = (i: number, patch: Partial<Row>) => {
      next = next.map((r, j) => (j === i ? { ...r, ...patch } : r));
      setRows(next);
    };
    // These are safety actions: dispatch every command immediately — never
    // gate one stove's command on another device's confirmation.
    const posted = await Promise.allSettled(
      targets.map((t) => performNow(t.name))
    );
    posted.forEach((res, i) => {
      // Only a server response proves the command didn't go out. A timeout or
      // dropped connection leaves delivery unknown, so those fall through to
      // confirm-by-state rather than claiming a hard failure.
      if (res.status === "rejected" && !isDeliveryUnknown(res.reason)) {
        update(i, {
          status: "failed",
          error:
            res.reason instanceof Error
              ? res.reason.message
              : String(res.reason),
        });
      }
    });
    // Then confirm each sensor's reported state, all concurrently. The POST
    // only queues an MQTT command, so a sensor counts as done only once it
    // reports the expected state; "unconfirmed" is an honest timeout, not a
    // known failure.
    await Promise.all(
      targets.map(async (t, i) => {
        if (next[i].status === "failed") return;
        if (!waitNow) {
          update(i, { status: "applied" });
          return;
        }
        // A throwing check must not reject this loop: `applying` is what
        // disables every exit from the dialog, so leaving it stuck would trap
        // the operator mid-safety-action with only a reload to get out.
        let confirmed = false;
        try {
          confirmed = await waitNow(t.name);
        } catch (err) {
          console.error("Stove state check failed", err);
        }
        update(
          i,
          confirmed
            ? { status: "applied" }
            : { status: "unconfirmed", error: NOT_CONFIRMED_ERROR }
        );
      })
    );
    setApplying(false);
  };

  const appliedCount = rows?.filter((r) => r.status === "applied").length ?? 0;
  const failedCount = rows?.filter((r) => r.status === "failed").length ?? 0;
  const unconfirmedCount =
    rows?.filter((r) => r.status === "unconfirmed").length ?? 0;
  const done = rows != null && !applying;
  const settledCount = appliedCount + failedCount + unconfirmedCount;

  const statusText = (r: Row) => {
    switch (r.status) {
      case "applying":
        return "Applying…";
      case "applied":
        return copy.appliedLabel;
      case "unconfirmed":
        return "Unconfirmed";
      default:
        return "Failed";
    }
  };

  const ActionIcon =
    action === "shutoff"
      ? PowerOffIcon
      : action === "lock"
        ? LockIcon
        : LockOpenIcon;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => {
        if (!applying) onClose();
      }}
    >
      <div
        ref={cardRef}
        className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-card bg-white text-ink p-6 shadow-depth border border-black/5"
        style={lockedHeight != null ? { height: lockedHeight } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-semibold inline-flex items-center gap-2">
            <ActionIcon className={copy.accent} />
            {rows == null
              ? copy.title
              : done
                ? copy.titleDone
                : copy.titleInProgress}
          </h2>
          <IconButton
            label="Close"
            onClick={onClose}
            disabled={applying}
            className="!w-8 !h-8 !bg-black/[0.04] !text-ink !border-black/10 hover:!bg-black/10"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {rows == null ? (
          <>
            <p className="text-ink-3 text-sm mb-4">
              {copy.confirmBody(buildingName)}
            </p>
            <ul className="rounded-control border border-black/10 bg-black/[0.02] divide-y divide-black/5 flex-1 min-h-0 max-h-64 overflow-y-auto">
              {targets.map((t) => (
                <li
                  key={t.name}
                  className="flex items-center justify-between text-sm px-4 py-2"
                >
                  <span className="font-medium">{t.displayName || t.name}</span>
                  {t.stateLabel && (
                    <span className="text-ink-3">{t.stateLabel}</span>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-black/10">
              <Button
                variant="ghost"
                size="sm"
                autoFocus
                onClick={onClose}
                className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
              >
                Cancel
              </Button>
              <Button
                variant={copy.buttonVariant}
                size="sm"
                onClick={applyToAll}
                disabled={targets.length === 0}
              >
                {copy.confirmButton}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            <div
              className="mb-4 flex flex-wrap items-center gap-3 text-sm shrink-0"
              aria-live="polite"
            >
              <span className="rounded-chip bg-alert-success/10 text-alert-success px-3 py-1 font-medium">
                {appliedCount} {copy.appliedLabel}
              </span>
              {unconfirmedCount > 0 && (
                <span className="rounded-chip bg-alert-warning/10 text-alert-warning px-3 py-1 font-medium">
                  {unconfirmedCount} Unconfirmed
                </span>
              )}
              {failedCount > 0 && (
                <span className="rounded-chip bg-alert-critical/10 text-alert-critical px-3 py-1 font-medium">
                  {failedCount} Failed
                </span>
              )}
              {applying && (
                <span className="text-ink-3 inline-flex items-center gap-2">
                  <Spinner size={16} />
                  Applying… ({settledCount}/{targets.length})
                </span>
              )}
            </div>
            <ul className="space-y-1 flex-1 min-h-0 overflow-y-auto">
              {rows.map((r) => (
                <li
                  key={r.target.name}
                  className="flex items-center justify-between text-sm border-b border-black/5 py-1.5"
                >
                  <span className="font-medium">
                    {r.target.displayName || r.target.name}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 ${STATUS_CLASS[r.status]}`}
                    title={r.error}
                  >
                    {r.status === "applying" && <Spinner size={14} />}
                    {statusText(r)}
                  </span>
                </li>
              ))}
            </ul>
            {done && (failedCount > 0 || unconfirmedCount > 0) && (
              <p className="text-ink-3 text-xs mt-3">
                {failedCount > 0 &&
                  "Failed stoves rejected the command — retry, or check them in person. "}
                {unconfirmedCount > 0 &&
                  "Unconfirmed stoves haven't reported the new state yet; the command may still apply. Refresh in a moment to check."}
              </p>
            )}
            {done && (
              <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-black/10">
                <Button variant="primary" size="sm" autoFocus onClick={onClose}>
                  Done
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
